<script setup lang="ts">
/**
 * Changer qui est prévenu sur une surveillance (geste 13, route 5 ;
 * `dialogueDestinataires` et `confirmationDestinataires` S:5418-5480).
 *
 * Ce que l'utilisateur doit vivre : il coche les membres prévenus, puis
 * confirme une phrase qui nomme qui sera prévenu ; retirer tout le monde se
 * dit « Plus personne ne sera prévenu », avec ce que cela fait. Une personne
 * déjà prévenue garde ses canaux ; une personne ajoutée prend ceux du type.
 * Rien ne part si la liste n'a pas changé. Un destinataire que la liste des
 * membres ne connaît pas reste dans la liste, nommé s'il est servi, jamais par
 * son identifiant.
 *
 * Le dialogue n'écrit rien lui-même : il rend le corps par `valider`.
 */
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import type { StatutMembres } from "@modules/alarmes-v2/stores/membres.store"
import type {
    AlarmesV2Destinataire,
    AlarmesV2DestinataireEcrit,
    AlarmesV2Membre,
    AlarmesV2Modification,
    AlarmesV2Personne,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    destinatairesEcrits,
    memesDestinataires,
} from "@modules/alarmes-v2/utils/ecritures"
import { nomDeLaPersonne } from "@modules/alarmes-v2/utils/journal"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()
const tr = t as unknown as Traduire

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La liste servie. */
        destinataires: AlarmesV2Destinataire[]
        /** Les membres lus, par identifiant. */
        membres: Record<string, AlarmesV2Membre>
        statutMembres: StatutMembres
        personnes?: Record<string, AlarmesV2Personne>
        enCours?: boolean
        refus?: string
    }>(),
    { personnes: () => ({}), enCours: false, refus: "" }
)

const emit = defineEmits<{
    valider: [corps: AlarmesV2Modification]
    fermer: []
}>()

/** La liste servie, dans la forme écrite ; `null` si elle n'est pas lisible. */
const avant = destinatairesEcrits(props.destinataires)

/** Les personnes cochées. */
const coches = ref<Set<string>>(new Set((avant ?? []).map((d) => d._id_user)))

/** L'étape : choisir, puis confirmer. */
const etape = ref<"choisir" | "confirmer">("choisir")
const rienAChanger = ref(false)

// ================================
// COMPUTED
// ================================
/** Une personne de la liste : un membre, ou un destinataire hors des membres. */
interface Personne {
    id: string
    nom: string
    role: string
}

/** Le nom d'une personne : le membre lu, sinon les personnes servies. */
function nomDe(id: string): string {
    const m = props.membres[id]
    const nom = [m?.prenom, m?.nom]
        .filter((x): x is string => typeof x === "string" && x !== "")
        .join(" ")
    return nom || nomDeLaPersonne(id, props.personnes, tr)
}

const getPersonnes = computed((): Personne[] => {
    const roles = MESSAGES_ALARMES_V2.prevenus.roles as Record<string, string>
    const ids = new Set([
        ...Object.keys(props.membres),
        ...(avant ?? []).map((d) => d._id_user),
    ])
    return [...ids]
        .map((id) => {
            const r = props.membres[id]?.role
            return {
                id,
                nom: nomDe(id),
                role: typeof r === "string" && r in roles ? roles[r] : "",
            }
        })
        .sort((a, b) => a.nom.localeCompare(b.nom, "fr"))
})

/** La liste à envoyer. */
const getApres = computed((): AlarmesV2DestinataireEcrit[] => {
    const parId = new Map((avant ?? []).map((d) => [d._id_user, d]))
    return getPersonnes.value
        .filter((p) => coches.value.has(p.id))
        .map((p) => parId.get(p.id) ?? { _id_user: p.id })
})

/** La phrase de confirmation (S:5462-5464). */
const getPhrase = computed((): string => {
    const noms = getApres.value.map((d) => nomDe(d._id_user))
    if (noms.length === 0)
        return t("alarmesV2.actions.destinatairesDialogue.personne")
    const liste =
        noms.length === 1
            ? noms[0]
            : `${noms.slice(0, -1).join(", ")} et ${noms[noms.length - 1]}`
    return noms.length > 1
        ? t("alarmesV2.actions.destinatairesDialogue.seront", { noms: liste })
        : t("alarmesV2.actions.destinatairesDialogue.sera", { noms: liste })
})

// ================================
// HANDLERS
// ================================
function onCocher(id: string, coche: boolean): void {
    if (props.enCours) return
    const s = new Set(coches.value)
    if (coche) s.add(id)
    else s.delete(id)
    coches.value = s
    rienAChanger.value = false
}

function onContinuer(): void {
    if (!avant) return
    if (memesDestinataires(avant, getApres.value)) {
        rienAChanger.value = true
        return
    }
    etape.value = "confirmer"
}

function onConfirmer(): void {
    if (props.enCours || !avant) return
    emit("valider", { destinataires: getApres.value })
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <CadreDialogue
        :titre="
            etape === 'choisir'
                ? t('alarmesV2.actions.destinatairesDialogue.titre')
                : t('alarmesV2.actions.destinatairesDialogue.confirmerTitre')
        "
        :bloque="enCours"
        @fermer="emit('fermer')"
    >
        <template #intro>
            <p v-if="etape === 'choisir'" class="text-sm text-surface-600">
                {{ t("alarmesV2.actions.destinatairesDialogue.texte") }}
            </p>
        </template>

        <p
            v-if="!avant"
            class="text-sm text-surface-800"
            data-test="non-modifiable"
        >
            {{ t("alarmesV2.actions.destinatairesDialogue.nonModifiable") }}
        </p>
        <p
            v-else-if="statutMembres !== 'pret'"
            class="text-sm text-surface-800"
            data-test="membres-indisponibles"
        >
            {{
                t(
                    "alarmesV2.actions.destinatairesDialogue.membresIndisponibles"
                )
            }}
        </p>
        <template v-else-if="etape === 'choisir'">
            <ul class="flex flex-col gap-1">
                <li
                    v-for="p in getPersonnes"
                    :key="p.id"
                    class="flex items-center justify-between gap-3 border-b border-surface-100 py-1 text-sm"
                    data-test="personne"
                >
                    <span>
                        <span class="block text-surface-900">{{ p.nom }}</span>
                        <span
                            v-if="p.role"
                            class="block text-xs text-surface-600"
                            >{{ p.role }}</span
                        >
                    </span>
                    <label class="flex items-center gap-1 text-sm">
                        <input
                            type="checkbox"
                            :checked="coches.has(p.id)"
                            :data-test="`cocher-${p.id}`"
                            @change="
                                onCocher(
                                    p.id,
                                    ($event.target as HTMLInputElement).checked
                                )
                            "
                        />
                        {{
                            t("alarmesV2.actions.destinatairesDialogue.prevenu")
                        }}
                    </label>
                </li>
            </ul>
            <p
                v-if="rienAChanger"
                class="text-sm text-surface-700"
                role="status"
                data-test="rien-a-changer"
            >
                {{ t("alarmesV2.actions.destinatairesDialogue.rienAChanger") }}
            </p>
        </template>
        <template v-else>
            <p class="text-sm" data-test="phrase">{{ getPhrase }}</p>
            <p
                v-if="getApres.length === 0"
                class="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-800"
                data-test="personne-danger"
            >
                {{
                    t("alarmesV2.actions.destinatairesDialogue.personneDanger")
                }}
            </p>
        </template>

        <p v-if="enCours" class="text-sm text-surface-700" role="status">
            {{ t("alarmesV2.actions.envoi") }}
        </p>
        <p
            v-else-if="refus"
            class="text-sm text-red-800"
            role="alert"
            data-test="refus"
        >
            {{ refus }}
        </p>

        <template #pied>
            <button
                type="button"
                class="rounded border border-surface-300 px-3 py-1.5 text-sm"
                :aria-disabled="enCours ? 'true' : undefined"
                data-focus-initial
                data-test="annuler"
                @click="!enCours && emit('fermer')"
            >
                {{ t("alarmesV2.actions.annuler") }}
            </button>
            <button
                v-if="avant && statutMembres === 'pret' && etape === 'choisir'"
                type="button"
                class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                data-test="continuer"
                @click="onContinuer"
            >
                {{ t("alarmesV2.actions.continuer") }}
            </button>
            <button
                v-else-if="avant && etape === 'confirmer'"
                type="button"
                class="rounded border border-primary-600 bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                :aria-disabled="enCours ? 'true' : undefined"
                data-test="confirmer"
                @click="onConfirmer"
            >
                {{ t("alarmesV2.actions.confirmer") }}
            </button>
        </template>
    </CadreDialogue>
</template>
